/**
 * OverviewTab — prospect info panel + screening criteria + pending proposals.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 *
 * Previously, screening-criteria and proposals were fetched unconditionally on page mount.
 * Now lazy — both queries are gated on isActive so they only fire when this tab is open.
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  fetchScreeningCriteria,
  fetchProposals,
  updateLead,
  confirmProposal,
  rejectProposal,
} from '../../../lib/leasing.api';
import type {
  LeadDetail,
  ScreeningCriteria,
  AppointmentProposal,
} from './shared';
import { usePropertyTimezone } from '../../../hooks/usePropertyTimezone';
import { formatTimestamp, formatCalendarDate } from '../../../utils/date';
import { asCalendarDateString, asIsoDateTimeString } from '@resida/shared';
import { CaseEscalationSection } from '../../../components/agent/CaseEscalationSection';
import { AgentFeedbackReportsList } from '../../../components/agent/AgentFeedbackReportsList';
import { DOLLAR_INPUT } from '../../../lib/dollar-input';
import type {
  AgentEnablementDecision as AgentEnablement,
  AgentReviewDecision as AgentReview,
  PolicySource,
  AgentPolicyResponse as AgentPolicyState,
} from '@resida/api-types/agent';

// ---------------------------------------------------------------------------
// Agent Policy section (BR-ENABLE-CASCADE)
// Types are derived from the boundary contract in `@resida/api-types/agent`.
// ---------------------------------------------------------------------------

async function getLeadAgentPolicy(leadId: string): Promise<AgentPolicyState> {
  const response = await fetch(
    `/landlord/api/leads/${encodeURIComponent(leadId)}/agent-policy`,
    { method: 'GET', credentials: 'include' }
  );
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<AgentPolicyState>;
}

async function patchLeadAgentPolicy(
  leadId: string,
  body: { enablement?: AgentEnablement; review?: AgentReview }
): Promise<AgentPolicyState> {
  const response = await fetch(
    `/landlord/api/leads/${encodeURIComponent(leadId)}/agent-policy`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json() as { message?: string };
      if (err.message) message = err.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  return response.json() as Promise<AgentPolicyState>;
}

function formatLeadPolicySource(source: PolicySource): string {
  if (source === 'landlord') return 'landlord default';
  if (source === 'listing') return 'listing policy';
  if (source === 'lead') return 'lead policy';
  return 'system default';
}

interface LeadAgentPolicySectionProps {
  leadId: string;
}

function LeadAgentPolicySection({ leadId }: LeadAgentPolicySectionProps) {
  const [enablement, setEnablement] = useState<AgentEnablement>('inherit');
  const [review, setReview] = useState<AgentReview>('inherit');
  const [resolvedEnablement, setResolvedEnablement] = useState<'enabled' | 'disabled'>('disabled');
  const [resolvedReview, setResolvedReview] = useState<'required' | 'not_required'>('required');
  const [enablementSource, setEnablementSource] = useState<PolicySource>('system');
  const [reviewSource, setReviewSource] = useState<PolicySource>('system');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // True once we've confirmed the selects reflect server state (loaded or saved).
  const [hasSavedPolicy, setHasSavedPolicy] = useState(false);
  // True while the initial GET is in flight — selects are disabled during this period.
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  // True if the most recent GET load failed — prevents accidental save with wrong defaults.
  const [loadFailed, setLoadFailed] = useState(false);

  // CF-2/CF-3 fix: expose loadPolicy via useCallback so the Retry button can call it directly.
  const loadPolicy = useCallback(async () => {
    setLoadingPolicy(true);
    setLoadFailed(false);
    setError(null);
    try {
      const stored = await getLeadAgentPolicy(leadId);
      setEnablement(stored.enablement);
      setReview(stored.review);
      setResolvedEnablement(stored.resolvedEnablement);
      setResolvedReview(stored.resolvedReview);
      setEnablementSource(stored.enablementSource);
      setReviewSource(stored.reviewSource);
      // Mark as loaded from server — effective labels can now be shown.
      setHasSavedPolicy(true);
    } catch (err) {
      console.error('Failed to load lead agent policy:', err);
      setError('Could not load agent policy settings. Refresh the page to try again.');
      setLoadFailed(true);
    } finally {
      setLoadingPolicy(false);
    }
  }, [leadId]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const result = await patchLeadAgentPolicy(leadId, { enablement, review });
      // Update from response (source of truth per upstream contract)
      setEnablement(result.enablement);
      setReview(result.review);
      setResolvedEnablement(result.resolvedEnablement);
      setResolvedReview(result.resolvedReview);
      setEnablementSource(result.enablementSource);
      setReviewSource(result.reviewSource);
      setHasSavedPolicy(true);
      setSuccessMsg('Agent policy saved.');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save agent policy');
    } finally {
      setSaving(false);
    }
  };

  // B4: show the actual effective value from the cascade resolver, not a hardcoded guess.
  const enablementInheritNote = enablement === 'inherit' && hasSavedPolicy
    ? `Inherited from: ${formatLeadPolicySource(enablementSource)} — effective: agent ${resolvedEnablement === 'enabled' ? 'enabled' : 'disabled'}`
    : null;

  const reviewInheritNote = review === 'inherit' && hasSavedPolicy
    ? `Inherited from: ${formatLeadPolicySource(reviewSource)} — effective: review replies before they send: ${resolvedReview === 'required' ? 'Yes' : 'No'}`
    : null;

  return (
    <section
      data-testid="lead-agent-policy-section"
      className="mb-6 rounded-lg border border-border bg-card p-5"
      aria-labelledby="lead-agent-policy-title"
    >
      <h2 id="lead-agent-policy-title" className="text-base font-semibold mb-1">
        Agent Policy
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Override the agent behaviour for this specific lead. A value of &ldquo;Inherit&rdquo; falls back
        to the listing-level policy, then the landlord default or system default. The effective value
        is shown below each field.
      </p>

      {loadingPolicy ? (
        <div className="space-y-3" data-testid="lead-agent-policy-loading" aria-busy="true">
          <Skeleton className="h-9 w-full sm:w-64" />
          <Skeleton className="h-9 w-full sm:w-64" />
          <Skeleton className="h-8 w-24" />
        </div>
      ) : (
      <form
        onSubmit={(e) => void handleSave(e)}
        className="space-y-4"
        data-testid="lead-agent-policy-form"
      >
        {/* Enablement */}
        <div>
          <label
            htmlFor="lead-agent-enablement"
            className="block text-sm font-medium mb-1"
          >
            Agent enablement
          </label>
          <select
            id="lead-agent-enablement"
            data-testid="lead-agent-enablement-select"
            value={enablement}
            onChange={(e) => setEnablement(e.target.value as AgentEnablement)}
            disabled={saving}
            className="w-full sm:w-64 border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
            aria-describedby={enablementInheritNote ? 'lead-agent-enablement-note' : undefined}
          >
            <option value="inherit">Inherit from listing</option>
            <option value="enabled">Enabled — agent active for this lead</option>
            <option value="disabled">Disabled — agent off for this lead</option>
          </select>
          {enablementInheritNote && (
            <p
              id="lead-agent-enablement-note"
              data-testid="lead-agent-enablement-inherit-note"
              className="mt-1 text-xs text-muted-foreground"
            >
              {enablementInheritNote}
            </p>
          )}
          {hasSavedPolicy && enablement !== 'inherit' && (
            <p
              data-testid="lead-agent-enablement-effective"
              className="mt-1 text-xs text-muted-foreground"
            >
              Effective: {resolvedEnablement === 'enabled' ? 'Agent enabled' : 'Agent disabled'} at lead scope
            </p>
          )}
        </div>

        {/* Review gate */}
        <div>
          <label
            htmlFor="lead-agent-review"
            className="block text-sm font-medium mb-1"
          >
            Review replies before they send
          </label>
          <select
            id="lead-agent-review"
            data-testid="lead-agent-review-select"
            value={review}
            onChange={(e) => setReview(e.target.value as AgentReview)}
            disabled={saving}
            className="w-full sm:w-64 border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
            aria-describedby={reviewInheritNote ? 'lead-agent-review-note' : undefined}
          >
            <option value="inherit">Inherit from listing</option>
            <option value="required">Yes — hold drafts for your review</option>
            <option value="not_required">No — deliver drafts automatically</option>
          </select>
          {reviewInheritNote && (
            <p
              id="lead-agent-review-note"
              data-testid="lead-agent-review-inherit-note"
              className="mt-1 text-xs text-muted-foreground"
            >
              {reviewInheritNote}
            </p>
          )}
          {hasSavedPolicy && review !== 'inherit' && (
            <p
              data-testid="lead-agent-review-effective"
              className="mt-1 text-xs text-muted-foreground"
            >
              Effective: Review replies before they send: {resolvedReview === 'required' ? 'Yes' : 'No'} at lead scope
            </p>
          )}
        </div>

        {error && (
          <div
            role="alert"
            data-testid="lead-agent-policy-error"
            className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          >
            <p>{error}</p>
            {loadFailed && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                data-testid="lead-agent-policy-retry"
                onClick={() => void loadPolicy()}
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {successMsg && (
          <div
            role="status"
            aria-live="polite"
            data-testid="lead-agent-policy-success"
            className="p-3 bg-success/10 border border-success/25 rounded-md text-success text-sm"
          >
            {successMsg}
          </div>
        )}

        <Button
          type="submit"
          size="sm"
          disabled={saving || loadFailed}
          data-testid="lead-agent-policy-save"
          aria-label="Save agent policy for this lead"
        >
          {saving ? 'Saving…' : 'Save policy'}
        </Button>
      </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OverviewTabProps {
  lead: LeadDetail;
  onLeadUpdated: () => void;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// ProspectInfoPanel (local — small enough to keep here)
// ---------------------------------------------------------------------------

interface ProspectInfoPanelProps {
  lead: LeadDetail;
  onUpdated: () => void;
  screeningCriteria: ScreeningCriteria | null | undefined;
}

function ProspectInfoPanel({ lead, onUpdated, screeningCriteria }: ProspectInfoPanelProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(lead.prospectName ?? '');
  const [phone, setPhone] = useState(lead.prospectPhone ?? '');
  const [income, setIncome] = useState(
    lead.claimedMonthlyIncomeCents != null ? String(lead.claimedMonthlyIncomeCents / 100) : ''
  );
  const [householdSize, setHouseholdSize] = useState(
    lead.claimedHouseholdSize != null ? String(lead.claimedHouseholdSize) : ''
  );
  const [pets, setPets] = useState(
    lead.claimedPets != null ? String(lead.claimedPets) : ''
  );
  const [moveInDate, setMoveInDate] = useState(
    lead.claimedMoveInDate ? lead.claimedMoveInDate.slice(0, 10) : ''
  );
  const [notes, setNotes] = useState(lead.claimedNotes ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls =
    'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);
    try {
      await updateLead(lead.id, {
        prospectName: name || null,
        prospectPhone: phone || null,
        claimedMonthlyIncomeCents: income ? Math.round(parseFloat(income) * 100) : null,
        claimedHouseholdSize: householdSize ? parseInt(householdSize, 10) : null,
        claimedPets: pets ? parseInt(pets, 10) : null,
        claimedMoveInDate: moveInDate || null,
        claimedNotes: notes || null,
      });
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prospect info');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper for criteria display
  const fmt = (val: unknown): string => {
    if (val == null) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return String(val);
  };

  // Prospect claimed values for criteria comparison
  const prospectIncome =
    lead.claimedMonthlyIncomeCents != null
      ? `$${(lead.claimedMonthlyIncomeCents / 100).toLocaleString('en-US')}/mo`
      : '—';
  const prospectHousehold =
    lead.claimedHouseholdSize != null ? String(lead.claimedHouseholdSize) : '—';

  return (
    <>
      <section data-testid="lead-prospect-info" className="mb-6 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Inquiry notes (from initiating prospect)</h2>
          {!editing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="lead-prospect-info-edit"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>

        {editing ? (
          <form onSubmit={(e) => void handleSave(e)} className="space-y-4" data-testid="lead-prospect-info-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="lead-name" className="block text-sm font-medium mb-1">
                  Prospect Name
                </label>
                <input
                  id="lead-name"
                  data-testid="lead-prospect-info-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="lead-phone" className="block text-sm font-medium mb-1">
                  Phone
                </label>
                <input
                  id="lead-phone"
                  data-testid="lead-prospect-info-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 000-0000"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label htmlFor="lead-income" className="block text-sm font-medium mb-1">
                  Monthly Income ($)
                </label>
                <input
                  id="lead-income"
                  data-testid="lead-prospect-info-income"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={income}
                  onChange={(e) => {
                    if (DOLLAR_INPUT.test(e.target.value)) setIncome(e.target.value);
                  }}
                  placeholder="5000"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="lead-household" className="block text-sm font-medium mb-1">
                  Household Size
                </label>
                <input
                  id="lead-household"
                  data-testid="lead-prospect-info-household"
                  type="number"
                  min="1"
                  value={householdSize}
                  onChange={(e) => setHouseholdSize(e.target.value)}
                  placeholder="2"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="lead-pets" className="block text-sm font-medium mb-1">
                  Pets
                </label>
                <input
                  id="lead-pets"
                  data-testid="lead-prospect-info-pets"
                  type="number"
                  min="0"
                  value={pets}
                  onChange={(e) => setPets(e.target.value)}
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="lead-move-in" className="block text-sm font-medium mb-1">
                  Move-in Date
                </label>
                <input
                  id="lead-move-in"
                  data-testid="lead-prospect-info-move-in"
                  type="date"
                  value={moveInDate}
                  onChange={(e) => setMoveInDate(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label htmlFor="lead-notes" className="block text-sm font-medium mb-1">
                Notes
              </label>
              <textarea
                id="lead-notes"
                data-testid="lead-prospect-info-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Additional notes…"
                className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y"
              />
            </div>

            {error && (
              <div
                data-testid="lead-prospect-info-error"
                className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
                role="alert"
              >
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button type="submit" disabled={isSaving} data-testid="lead-prospect-info-save">
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(false)}
                data-testid="lead-prospect-info-cancel"
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground mb-0.5">Email</dt>
              <dd className="font-medium">{lead.prospectEmail}</dd>
            </div>
            {lead.prospectPhone && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Phone</dt>
                <dd className="font-medium">{lead.prospectPhone}</dd>
              </div>
            )}
            {lead.claimedMonthlyIncomeCents != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Monthly Income</dt>
                <dd className="font-medium">
                  ${(lead.claimedMonthlyIncomeCents / 100).toLocaleString('en-US')}/mo
                </dd>
              </div>
            )}
            {lead.claimedHouseholdSize != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Household Size</dt>
                <dd className="font-medium">{lead.claimedHouseholdSize}</dd>
              </div>
            )}
            {lead.claimedPets != null && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Pets</dt>
                <dd className="font-medium">{lead.claimedPets}</dd>
              </div>
            )}
            {lead.claimedMoveInDate && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Move-in Date</dt>
                <dd className="font-medium">
                  {/* AP-002: a calendar date, NOT an instant. claimedMoveInDate is a
                      @db.Date serialized with toISOString(), so it arrives as
                      "YYYY-MM-DDT00:00:00.000Z"; formatCalendarDate parses YYYY-MM-DD, hence
                      the slice. Rendering it through new Date(...).toLocaleDateString() shifted
                      it a day backwards for any viewer west of UTC. */}
                  {formatCalendarDate(asCalendarDateString(lead.claimedMoveInDate.slice(0, 10)), {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </dd>
              </div>
            )}
            {lead.claimedNotes && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-muted-foreground mb-0.5">Notes</dt>
                <dd className="whitespace-pre-wrap">{lead.claimedNotes}</dd>
              </div>
            )}
            {lead.source && (
              <div>
                <dt className="text-muted-foreground mb-0.5">Source</dt>
                <dd className="font-medium capitalize">{lead.source.replace(/_/g, ' ')}</dd>
              </div>
            )}
            {!lead.prospectPhone &&
              lead.claimedMonthlyIncomeCents == null &&
              lead.claimedHouseholdSize == null &&
              lead.claimedPets == null &&
              !lead.claimedMoveInDate &&
              !lead.claimedNotes &&
              !lead.source && (
                <div className="col-span-2 sm:col-span-3">
                  <p className="text-sm text-muted-foreground italic">No additional prospect info recorded.</p>
                </div>
              )}
          </dl>
        )}
      </section>

      {/* Screening Criteria Comparison Panel */}
      <section
        data-testid="screening-criteria-panel"
        className="mb-6 rounded-lg border border-border bg-card p-5"
      >
        <h2 className="text-base font-semibold mb-4">Screening Criteria</h2>

        {screeningCriteria === undefined && (
          <p className="text-sm text-muted-foreground italic">Loading criteria…</p>
        )}

        {screeningCriteria === null && (
          <p className="text-sm text-muted-foreground italic">
            No screening criteria set for this listing yet.
          </p>
        )}

        {screeningCriteria != null && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-medium text-muted-foreground pb-2 pr-4 w-1/3">
                    Criterion
                  </th>
                  <th className="text-left font-medium text-muted-foreground pb-2 pr-4 w-1/3">
                    Listing Requirement
                  </th>
                  <th className="text-left font-medium text-muted-foreground pb-2 w-1/3">
                    Prospect Claimed
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr data-testid="criteria-row-income">
                  <td className="py-2 pr-4 text-muted-foreground">Min. Income Multiple</td>
                  <td className="py-2 pr-4 font-medium">
                    {screeningCriteria.minIncomeMultiple != null
                      ? `${screeningCriteria.minIncomeMultiple}×`
                      : '—'}
                  </td>
                  <td className="py-2 font-medium">{prospectIncome}</td>
                </tr>
                <tr data-testid="criteria-row-credit">
                  <td className="py-2 pr-4 text-muted-foreground">Min. Credit Score</td>
                  <td className="py-2 pr-4 font-medium">
                    {fmt(screeningCriteria.minCreditScore)}
                  </td>
                  <td className="py-2 text-muted-foreground italic">Not collected</td>
                </tr>
                <tr data-testid="criteria-row-occupants">
                  <td className="py-2 pr-4 text-muted-foreground">Max. Occupants</td>
                  <td className="py-2 pr-4 font-medium">
                    {fmt(screeningCriteria.maxOccupants)}
                  </td>
                  <td className="py-2 font-medium">{prospectHousehold}</td>
                </tr>
                <tr data-testid="criteria-row-smoking">
                  <td className="py-2 pr-4 text-muted-foreground">Smoking Policy</td>
                  <td className="py-2 pr-4 font-medium capitalize">
                    {screeningCriteria.smokingPolicy.replace(/_/g, ' ')}
                  </td>
                  <td className="py-2 text-muted-foreground italic">Not collected</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// PendingProposalsPanel (local)
// ---------------------------------------------------------------------------

interface PendingProposalsPanelProps {
  lead: LeadDetail;
  onUpdated: () => void;
  isActive: boolean;
}

function PendingProposalsPanel({ lead, onUpdated, isActive }: PendingProposalsPanelProps) {
  const propertyTimezone = usePropertyTimezone();
  const queryClient = useQueryClient();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState<Record<string, string>>({});
  const [showRejectForm, setShowRejectForm] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);

  // Previously fetched unconditionally; now lazy — tab must be active.
  const { data: proposalsData, isLoading, error } = useQuery({
    queryKey: ['proposals', lead.id],
    queryFn: () => fetchProposals(lead.id),
    enabled: isActive,
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['proposals', lead.id] });
    void queryClient.invalidateQueries({ queryKey: ['listing', lead.listingId, 'showings', 'for-lead', lead.id] });
  }, [queryClient, lead.id, lead.listingId]);

  const pendingProposals = (proposalsData ?? []).filter((p: AppointmentProposal) => p.status === 'pending');

  const handleConfirm = async (proposal: AppointmentProposal) => {
    setConfirmingId(proposal.id);
    setPanelError(null);
    try {
      await confirmProposal(lead.id, proposal.id);
      invalidate();
      onUpdated();
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 409) {
        setPanelError(`Scheduling conflict: ${e.message}`);
      } else {
        setPanelError(e.message || 'Failed to confirm proposal');
      }
    } finally {
      setConfirmingId(null);
    }
  };

  const handleReject = async (proposal: AppointmentProposal) => {
    setRejectingId(proposal.id);
    setPanelError(null);
    try {
      await rejectProposal(lead.id, proposal.id, rejectNotes[proposal.id] || undefined);
      setShowRejectForm(null);
      setRejectNotes((prev) => { const n = { ...prev }; delete n[proposal.id]; return n; });
      invalidate();
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Failed to reject proposal');
    } finally {
      setRejectingId(null);
    }
  };

  return (
    <section
      data-testid="proposals-panel"
      className="mb-6 rounded-lg border border-border bg-card p-5"
    >
      <h2 className="text-base font-semibold mb-4">Pending proposals</h2>

      {isLoading && <Skeleton className="h-16 w-full" />}

      {error && (
        <div
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          Failed to load proposals. Please refresh.
        </div>
      )}

      {panelError && (
        <div
          className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          {panelError}
        </div>
      )}

      {!isLoading && !error && pendingProposals.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No pending proposals.</p>
      )}

      {!isLoading && pendingProposals.length > 0 && (
        <ul className="space-y-3">
          {pendingProposals.map((proposal) => (
            <li
              key={proposal.id}
              data-testid={`proposal-row-${proposal.id}`}
              className="rounded-lg border border-border p-4"
            >
              <div className="mb-2">
                <p className="text-sm font-medium">
                  {/* AP-002a: an instant (@db.Timestamptz). A showing time shown in the
                      viewer's browser zone rather than the property's is wrong for any
                      landlord not sitting in the property's timezone. */}
                  Proposed: {formatTimestamp(asIsoDateTimeString(proposal.proposedFor), propertyTimezone, {
                    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}{' '}
                  <span className="text-muted-foreground font-normal">({proposal.durationMinutes} min)</span>
                </p>
                {proposal.proposedNotes && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Note: "{proposal.proposedNotes}"
                  </p>
                )}
              </div>

              {showRejectForm === proposal.id ? (
                <div className="space-y-2">
                  <label htmlFor={`reject-notes-${proposal.id}`} className="block text-xs font-medium">
                    Rejection note (optional)
                  </label>
                  <textarea
                    id={`reject-notes-${proposal.id}`}
                    value={rejectNotes[proposal.id] ?? ''}
                    onChange={(e) => setRejectNotes((prev) => ({ ...prev, [proposal.id]: e.target.value }))}
                    rows={2}
                    placeholder="Add a note to the prospect…"
                    className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      data-testid={`proposal-reject-submit-${proposal.id}`}
                      disabled={rejectingId === proposal.id}
                      onClick={() => void handleReject(proposal)}
                      aria-label="Confirm rejection of this proposal"
                    >
                      {rejectingId === proposal.id ? 'Rejecting…' : 'Confirm rejection'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowRejectForm(null)}
                      disabled={rejectingId === proposal.id}
                      aria-label="Cancel rejection"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`proposal-confirm-${proposal.id}`}
                    disabled={confirmingId === proposal.id}
                    onClick={() => void handleConfirm(proposal)}
                    aria-label="Confirm this appointment proposal"
                  >
                    {confirmingId === proposal.id ? 'Confirming…' : 'Confirm'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`proposal-reject-btn-${proposal.id}`}
                    onClick={() => setShowRejectForm(proposal.id)}
                    aria-label="Reject this appointment proposal"
                  >
                    Reject
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

export function OverviewTab({ lead, onLeadUpdated, isActive }: OverviewTabProps) {
  // Server-computed (DC-51): a current paid period on the unit OR the landlord's agent add-on.
  const { leasingAgentAvailable } = lead;
  const propertyTimezone = usePropertyTimezone();

  // Previously fetched unconditionally; now lazy — tab must be active.
  const { data: criteriaData } = useQuery({
    queryKey: ['screening-criteria', lead.listingId],
    queryFn: () => fetchScreeningCriteria(lead.listingId),
    enabled: isActive && !!lead.listingId,  // lazy: only when tab is active
    staleTime: 60_000,
  });

  return (
    <div>
      <ProspectInfoPanel
        lead={lead}
        onUpdated={onLeadUpdated}
        screeningCriteria={criteriaData?.screeningCriteria}
      />
      <PendingProposalsPanel
        lead={lead}
        onUpdated={onLeadUpdated}
        isActive={isActive}
      />
      {/* Escalation + paused-agent state (BR-AGENT-ESC-1, BR-AGENT-ESC-2) — gated on
          leasingAgentAvailable like the policy section below, but rendered as its own,
          structurally separate section (see CaseEscalationSection's own doc comment). */}
      {leasingAgentAvailable && (
        <CaseEscalationSection
          caseId={lead.case.id}
          testIdPrefix="lead"
          takeoverText="you took over this conversation directly"
          onChanged={onLeadUpdated}
        />
      )}
      {/* Agent policy section — gated on leasingAgentAvailable (BR-ENABLE-CASCADE) */}
      {leasingAgentAvailable && <LeadAgentPolicySection leadId={lead.id} />}
      {/* The landlord's own agent-feedback reports (RA-FB-REPORTED) — hidden when none. */}
      <div className="[&:not(:empty)]:mb-6">
        <AgentFeedbackReportsList caseId={lead.id} timezone={propertyTimezone} />
      </div>
    </div>
  );
}
